import type { ComponentProps, FC, JSX, MouseEvent } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import type { IDropdownItem, INavItemBadgeType } from "@similarweb/ui-components";
import { WebSourceDropdownItem, NavItemBadge } from "@similarweb/ui-components";

import type { IFilterBaseProps } from "app/components/filters-bar/FiltersBase";
import { FILTER_CHANGED_NOTIFICATION_WIDTH } from "app/components/filters-bar/FiltersBase";
import { i18nFilter } from "app/filters/i18nFilter";
import FilterChangedNotification from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltip";
import { EFilterChangedTooltipTypes } from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipTypes";

import { FiltersBarDropdown } from "../filters-bar-dropdown/FiltersBarDropdown";
import { FiltersBarDropdownButton } from "../filters-bar-dropdown/FiltersBarDropdownButton";

import "./WebSourceFilter.scss";

export interface IWebSourceFilterItem extends IDropdownItem {
    id: ComponentProps<typeof WebSourceDropdownItem>["id"];
    icon: IconName;
    disabled?: boolean;
    available?: boolean;
    badgeType?: INavItemBadgeType;
}

interface IWebSourceFilterProps extends IFilterBaseProps {
    items: IWebSourceFilterItem[];
    onChange?: (itemId: number) => void;
    selectedIds: { [key: string]: boolean };
}

const NewLabelWrapper = styled.span`
    margin-left: 4px;
    display: flex;
    align-items: center;
`;

const WebSourceFilterDropdownButton = styled.div.attrs((_) => ({
    className: "WebSourceFilter-dropdownButton",
}))`
    display: flex;
    align-items: center;
    justify-content: start;
    padding: 0;
    width: 100%;
    box-sizing: border-box;
    text-indent: 0;
`;

function createButton(
    icon: IconName,
    text: string,
    width: number | string,
    height: number | string,
    disabled: boolean,
    presentNotificationIndicator: boolean,
    presentNotificationTooltipKey: string,
) {
    const body = i18nFilter()(presentNotificationTooltipKey);
    // We don't want the dropdown to open for a disabled dropdown accompanied by Filter Changed Notification
    const onClick = (event: MouseEvent<HTMLDivElement>) => disabled && event.stopPropagation();
    return (
        <FiltersBarDropdownButton
            key={0}
            width={width}
            height={height}
            disabled={disabled}
        >
            <WebSourceFilterDropdownButton>
                <div className="WebSourceFilter-dropdownButton-icon">
                    <SWReactIcons iconName={icon} />
                </div>
                <div className="WebSourceFilter-dropdownButton-text">{text}</div>
                {!disabled && presentNotificationIndicator ? (
                    <FilterChangedNotification
                        width={FILTER_CHANGED_NOTIFICATION_WIDTH}
                        tooltipBody={body}
                        filterType={EFilterChangedTooltipTypes.WEB_SOURCE_PICKER}
                        onClick={onClick}
                    />
                ) : null}
            </WebSourceFilterDropdownButton>
        </FiltersBarDropdownButton>
    );
}

export const WebSourceFilter: FC<IWebSourceFilterProps> = ({ height = 70, width = 180, ...props }) => {
    const selectedId = Object.keys(props.selectedIds)[0];
    const selectedItem = props.items.find((item) => item.id === selectedId);
    const selectedText = selectedItem?.text;
    const button = createButton(
        // @ts-ignore FIXME: strict migration - selectedItem possibly undefined; selectedIds always reflects an existing item at render time
        selectedItem.icon,
        // @ts-ignore FIXME: strict migration - selectedText may be undefined when selectedItem is undefined; same invariant as above
        selectedText,
        width,
        height,
        props.disabled,
        props.presentNotificationIndicator,
        props.presentNotificationTooltipKey,
    );

    function getDropdownChildren(): JSX.Element[] {
        const children = [button];

        props.items.forEach((item) => {
            const { badgeType } = item;
            const customChildren = badgeType ? (
                <NewLabelWrapper>
                    <NavItemBadge type={badgeType} />
                </NewLabelWrapper>
            ) : null;

            children.push(
                <WebSourceDropdownItem
                    key={item.id}
                    {...item}
                    customChildren={customChildren}
                    wrapper={props.itemWrapper}
                />,
            );
        });

        return children;
    }

    return (
        <FiltersBarDropdown
            onClick={props?.onChange}
            selectedIds={props.selectedIds}
            closeOnItemClick={true}
            width={221}
            onToggle={props.onToggle}
            appendTo={props.appendTo}
            dropdownPopupPlacement={props.dropdownPopupPlacement}
        >
            {getDropdownChildren()}
        </FiltersBarDropdown>
    );
};

WebSourceFilter.displayName = "WebSourceFilter";
